import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Dropdown from '@/components/UI/Dropdown/Dropdown';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import InputText from '@/components/UI/InputText/InputText';
import InputTextRatePct from '@/components/UI/InputTextRatePct/InputTextRatePct';
import { AFF_VEHICLES, HELP_AREA, NUMERIC_FIELDS, TAB_FORMS_OPTIONS } from '@/helpers/constants';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignVehicles, setCampaignHelp, setOriginalValues, setCampaignVehiclesTechAssessment } from '@/redux/Campaign/campaign.action';
import { getCampaignVehicles, getCampaignHelp, getOriginalValues, getCampaignVehiclesTechAssessment, getCampaignInfo } from '@/redux/Campaign/campaign.reducer';
import { setAffectedVehiclesForms } from '@/redux/Forms/forms.action';
import { getAffectedVehiclesForms } from '@/redux/Forms/forms.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, OriginalValues, ReduxFormsState, TechnicalAssessment } from '@/types';
import styles from '../AffectedVehicles.module.scss';

type TechAssessmentProps = {
  originalValues: OriginalValues;
  setOriginValues: (originalValues: OriginalValues) => void;
  techAssessment: TechnicalAssessment;
  setTechAssessment: (techAssessment: TechnicalAssessment) => void;
  forms: any;
  setForms: (forms: ReduxFormsState) => void;
  campaignInfo: CampaignInfo;
  setHelpArea: (area: string) => void;
  headerLanguage: string;
};

const TechAssessment: React.FC<TechAssessmentProps> = (props) => {
  const { originalValues, setOriginValues, techAssessment, setTechAssessment, forms, setForms, setHelpArea,
    campaignInfo, headerLanguage } = props;
  const { t } = useTranslation();
  const checkDisabled = useCheckDisabled(true);
  const { addToast } = useToastContext();
  const estimatedNOKRatePct: string = 'estimatedNOKRatePct';
  const vehicleVolumeNumbers: string = 'vehicleVolumeNumbers';
  const noDecimalFields: string[] = [estimatedNOKRatePct, vehicleVolumeNumbers];

  useEffect(() => {
    if (campaignInfo?.creationDate) {
      campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_AFF_VEHICLES, campaignInfo?.creationDate).then((res) => {
        setForms(res.form);
      }).catch((error) => addToast(error.message, 'ERROR'));
    }
  }, [campaignInfo?.creationDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSetOriginValues = () => {
    const values = {
      ...originalValues,
      hasChange: true,
      changesIn: {
        ...originalValues.changesIn,
        technicalAssessment: true,
      },
    };
    setOriginValues(values);
  };

  const fieldIsValid = (label: string, value: number | string) => {
    const valueNumber = Number(value);

    if (label === estimatedNOKRatePct) {
      return valueNumber >= 0 && valueNumber <= 100;
    }

    return true;
  };

  const updateState = (label: string, value: string | number) => {
    let formatValue = value;

    if (typeof value === 'string' && label && noDecimalFields.includes(label)) {
      formatValue = value.replace(/[.,]/g, '');
    }

    if (fieldIsValid(label, formatValue)) {
      const newState = {
        ...techAssessment,
        [label]: formatValue,
      };

      onSetOriginValues();
      setTechAssessment(newState);
    }
  };

  const orderOptions = (options) => {
    const keys = Object.keys(options || {}).reverse();

    return keys?.reduce((acc, label) => ({
      ...acc,
      [label]: options?.[label],
    }), {});
  };

  return (
    <AccordionPanel
      title={t('affectedVehicles.technicalAssessmentOfTheTopic')}
      handleClick={() => setHelpArea(HELP_AREA.TECHNICAL_ASSESSMENT)}
    >
      {!window.location.pathname.includes('sub-campaign') && (
        <div className={styles.OneColumns}>
          <InfoMsg msg={t('affectedVehicles.techAssessmentMessage')} />
        </div>
      )}
      <div className={styles.FourColumns}>
        <InputTextRatePct
          disabled={checkDisabled}
          id="input-number-estimated-nok-rate-pct"
          type="number"
          label={t('affectedVehicles.estimatedNOKRatePct')}
          placeholder={`${t('affectedVehicles.numberMessage')} (min: 0, max: 100)`}
          handleChange={(ev) => updateState('estimatedNOKRatePct', ev.target.value)}
          value={String(techAssessment?.estimatedNOKRatePct || '')}
          fieldPath={['affectedVehicles', 'technicalAssessment', 'estimatedNOKRatePct']}
          maxLength={3}
        />
      </div>
      <div className={styles.FourColumns}>
        <InputText
          disabled={checkDisabled}
          id={NUMERIC_FIELDS.INPUT_VEHICLE_VOLUME_NO}
          type="number"
          label={t('affectedVehicles.vehicleVolumeNumbers')}
          placeholder={t('affectedVehicles.numberMessage')}
          handleChange={(ev) => updateState('vehicleVolumeNumbers', ev.target.value)}
          value={String(techAssessment?.vehicleVolumeNumbers || '')}
          fieldPath={['affectedVehicles', 'technicalAssessment', 'vehicleVolumeNumbers']}
          maxLength={32}
        />
      </div>
      <div className={styles.FourColumns}>
        <Dropdown
          disabled={checkDisabled}
          id={AFF_VEHICLES.AMG}
          label={t('affectedVehicles.amgVehicles')}
          options={orderOptions(forms?.amgVehicles?.options)}
          value={techAssessment?.amgVehicles || ''}
          handleChange={(event) => updateState(AFF_VEHICLES.AMG, event.target.value)}
          language={headerLanguage}
          fieldPath={['affectedVehicles', 'technicalAssessment', AFF_VEHICLES.AMG]}
        />
      </div>
    </AccordionPanel>
  );
};

const mapStateToProps = (state) => ({
  campaignHelp: getCampaignHelp(state),
  campaignInfo: getCampaignInfo(state),
  originalValues: getOriginalValues(state),
  series: getCampaignVehicles(state)?.series || [],
  techAssessment: getCampaignVehiclesTechAssessment(state),
  forms: getAffectedVehiclesForms(state),
  headerLanguage: getHeaderLanguage(state),
});

const actionCreators = {
  setAffectedVehicles: setCampaignVehicles,
  setHelp: setCampaignHelp,
  setOriginValues: setOriginalValues,
  setTechAssessment: setCampaignVehiclesTechAssessment,
  setForms: setAffectedVehiclesForms,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(TechAssessment);
